import { CombinedTable, Table } from "@/types/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Users, Link2, Trash2, RotateCcw, Pencil, Check, X, Minus, Plus, MapPin, Info } from "lucide-react";
import { useState, useEffect } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface CombinedTableCardProps {
  combinedTable: CombinedTable;
  allTables: Table[];
  onToggleActive: (id: string) => void;
  onToggleBookable?: (id: string) => void;
  onDelete: (id: string) => void;
  onRecalculate: (id: string) => void;
  onUpdateCapacity?: (id: string, newCapacity: number) => Promise<void>;
  onUpdateName?: (id: string, newName: string) => Promise<void>;
}

export function CombinedTableCard({
  combinedTable,
  allTables,
  onToggleActive,
  onToggleBookable,
  onDelete,
  onRecalculate,
  onUpdateCapacity,
  onUpdateName,
}: CombinedTableCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditingCapacity, setIsEditingCapacity] = useState(false);
  const [editedCapacity, setEditedCapacity] = useState(combinedTable.totalCapacity);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(combinedTable.name);

  // Reset edited values when combined table changes
  useEffect(() => {
    setEditedCapacity(combinedTable.totalCapacity);
  }, [combinedTable.totalCapacity]);

  useEffect(() => {
    setEditedName(combinedTable.name);
  }, [combinedTable.name]);

  const componentTables = allTables.filter((t) =>
    combinedTable.tableIds.includes(t.id)
  );

  const hasInactiveTable = componentTables.some((t) => !t.isActive);
  const hasNonBookableTable = componentTables.some((t) => t.isBookable === false);
  const nonBookableNames = componentTables.filter(t => t.isActive && t.isBookable === false).map(t => t.name);
  const isAvailable = combinedTable.isActive && !hasInactiveTable;

  // Calculate the maximum capacity (sum of component tables)
  const calculatedCapacity = componentTables.reduce((sum, t) => sum + t.capacity, 0);
  
  // Check if current capacity differs from calculated
  const hasCustomCapacity = combinedTable.totalCapacity !== calculatedCapacity;

  const handleStartEdit = () => {
    setEditedCapacity(combinedTable.totalCapacity);
    setIsEditingCapacity(true);
  };

  const handleCancelEdit = () => {
    setEditedCapacity(combinedTable.totalCapacity);
    setIsEditingCapacity(false);
  };

  const handleSaveCapacity = async () => {
    if (!onUpdateCapacity) return;
    
    // Validate: capacity must be at least 1 (no max limit - allows exceeding physical capacity)
    const validCapacity = Math.max(1, editedCapacity);
    
    if (validCapacity !== combinedTable.totalCapacity) {
      await onUpdateCapacity(combinedTable.id, validCapacity);
    }
    setIsEditingCapacity(false);
  };
  
  // Check if capacity exceeds physical sum of tables
  const exceedsPhysicalCapacity = combinedTable.totalCapacity > calculatedCapacity;

  const handleStartEditName = () => {
    setEditedName(combinedTable.name);
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setEditedName(combinedTable.name);
    setIsEditingName(false);
  };

  const handleSaveName = async () => {
    if (!onUpdateName) return;
    
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== combinedTable.name) {
      await onUpdateName(combinedTable.id, trimmedName);
    }
    setIsEditingName(false);
  };

  return (
    <Card
      className={`hover:shadow-md transition-all ${
        isHovered ? "ring-2 ring-primary/50" : ""
      } ${!isAvailable ? "opacity-60" : ""} ${combinedTable.isBookable === false ? "border-amber-300 border-dashed" : ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="space-y-1 flex-1">
          <CardTitle className="text-lg font-semibold">
            {isEditingName ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="h-8 text-lg font-semibold w-full"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveName();
                    if (e.key === "Escape") handleCancelEditName();
                  }}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 flex-shrink-0"
                  onClick={handleSaveName}
                >
                  <Check className="w-4 h-4 text-green-600" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 flex-shrink-0"
                  onClick={handleCancelEditName}
                >
                  <X className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-primary flex-shrink-0" />
                <span>{combinedTable.name}</span>
                {onUpdateName && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 flex-shrink-0"
                    onClick={handleStartEditName}
                    title="Modifier le nom"
                  >
                    <Pencil className="w-3 h-3" />
                  </Button>
                )}
              </div>
            )}
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant={isAvailable ? "default" : "secondary"}
              className={isAvailable ? "bg-green-500" : "bg-gray-400"}
            >
              {isAvailable ? "Disponible" : hasInactiveTable ? "Table(s) désactivée(s)" : "Désactivée"}
            </Badge>
            {combinedTable.isBookable === false && (
              <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                Hors réservation
              </Badge>
            )}
            {hasNonBookableTable && combinedTable.isBookable !== false && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="text-xs text-muted-foreground border-muted cursor-help">
                      Sous-table(s) HR
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{nonBookableNames.join(', ')} {nonBookableNames.length > 1 ? 'sont' : 'est'} hors réservation individuellement, mais cette combinaison reste réservable</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {hasCustomCapacity && !exceedsPhysicalCapacity && (
              <Badge variant="outline" className="text-xs">
                Capacité ajustée
              </Badge>
            )}
            {exceedsPhysicalCapacity && (
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-400">
                Dépasse physique
              </Badge>
            )}
            {(() => {
              const componentZoneIds = componentTables.map((t) => t.zoneId ?? null);
              const distinct = Array.from(new Set(componentZoneIds));
              const allSame = distinct.length === 1 && distinct[0] != null;
              const someHaveZone = componentZoneIds.some((z) => z != null);
              if (allSame) {
                return (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="text-xs gap-1 cursor-help">
                          <MapPin className="w-3 h-3" /> Zone héritée
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Toutes les sous-tables partagent la même zone réservable. La combinaison hérite automatiquement de cette zone.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              }
              if (someHaveZone) {
                return (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="text-xs gap-1 cursor-help text-muted-foreground">
                          <Info className="w-3 h-3" /> Aucune zone (mixte)
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Les sous-tables ont des zones différentes (ou certaines sans zone). Pour que la combinaison hérite d'une zone, toutes les sous-tables doivent avoir la même zone.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              }
              return null;
            })()}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onRecalculate(combinedTable.id)}
            title="Réinitialiser à la capacité maximale"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(combinedTable.id)}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Capacity section with inline editing */}
          <div className="flex items-center text-muted-foreground">
            <Users className="w-4 h-4 mr-2 flex-shrink-0" />
            {isEditingCapacity ? (
              <div className="flex items-center gap-2 flex-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => setEditedCapacity(Math.max(1, editedCapacity - 1))}
                  disabled={editedCapacity <= 1}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="text-lg font-semibold min-w-[2rem] text-center">{editedCapacity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => setEditedCapacity(editedCapacity + 1)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
                <span className="text-sm text-muted-foreground">({calculatedCapacity} physique)</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  onClick={handleSaveCapacity}
                >
                  <Check className="w-4 h-4 text-green-600" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  onClick={handleCancelEdit}
                >
                  <X className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-1">
                <span className="text-sm font-medium">
                  {combinedTable.totalCapacity} places
                </span>
                {hasCustomCapacity && (
                  <span className="text-xs text-muted-foreground">
                    (max: {calculatedCapacity})
                  </span>
                )}
                {onUpdateCapacity && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 ml-1"
                    onClick={handleStartEdit}
                    title="Modifier la capacité"
                  >
                    <Pencil className="w-3 h-3" />
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Tables composantes */}
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-2">Composée de:</p>
            <div className="flex flex-wrap gap-1">
              {componentTables.map((table) => {
                const isNonBookable = table.isActive && table.isBookable === false;
                const badge = (
                  <Badge
                    key={table.id}
                    variant="outline"
                    className={`text-xs ${
                      !table.isActive ? "opacity-50 line-through" : ""
                    } ${!table.isActive ? "" : isNonBookable ? "border-amber-400 text-amber-700 bg-amber-50 cursor-help" : ""} ${isHovered ? "bg-primary/10" : ""}`}
                  >
                    {table.name} ({table.capacity}p){isNonBookable ? " (HR)" : ""}
                  </Badge>
                );
                return isNonBookable ? (
                  <TooltipProvider key={table.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>{badge}</TooltipTrigger>
                      <TooltipContent>
                        <p>{table.name} est marquée hors réservation individuellement</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : badge;
              })}
            </div>
          </div>

          {hasInactiveTable && (
            <p className="text-xs text-destructive">
              ⚠️ Une ou plusieurs tables composantes sont désactivées
            </p>
          )}

          {/* Switch Hors réservation */}
          {onToggleBookable && (
            <div className="flex items-center justify-between pt-2 border-t">
              <span className="text-sm text-muted-foreground">
                Hors réservation
              </span>
              <Switch
                checked={combinedTable.isBookable === false}
                onCheckedChange={() => onToggleBookable(combinedTable.id)}
              />
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t">
            <span className="text-sm text-muted-foreground">
              {combinedTable.isActive ? "Activée" : "Désactivée"}
            </span>
            <Switch
              checked={combinedTable.isActive}
              onCheckedChange={() => onToggleActive(combinedTable.id)}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}